import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../hooks/useAuth";
import { useDialogFocus } from "../hooks/useDialogFocus";
import { ApiError } from "../services/api";
import { getOrganizationMembers } from "../services/calendar";
import { notifyStaff } from "../services/notify";
import type { OrganizationMember } from "../types/api";
import type { NotifyStaffResponse, StaffTagItemType } from "../types/notify";
import {
  MAX_NOTIFY_RECIPIENTS,
  buildNoAlertMessage,
  excludeSelf,
} from "../utils/notifyResult";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { ModalCloseButton } from "./ModalCloseButton";
import "./NotifyStaffModal.css";

interface NotifyStaffModalProps {
  isOpen: boolean;
  prospectId: string;
  itemType: StaffTagItemType;
  /** The video or note id. */
  itemId: string;
  /** Names the player, when the caller knows it. */
  subject?: string | null;
  onClose: () => void;
}

/**
 * Pick colleagues to tag on a player's video or note. Each gets a push on their phone that opens
 * the item. The web has no toast, so the result ("Sent", and who will not get a phone alert) is
 * shown in the dialog before it closes.
 */
export const NotifyStaffModal = ({ isOpen, ...props }: NotifyStaffModalProps) => {
  // The body mounts per open, so every open starts with a clean selection and a fresh list.
  if (!isOpen) return null;
  return <NotifyStaffModalBody {...props} />;
};

type LoadState = "loading" | "ready" | "error";

const sendErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError && err.statusCode === 404) return "That video or note was removed";
  return "Could not send. Check your connection and try again.";
};

const NotifyStaffModalBody = ({
  prospectId,
  itemType,
  itemId,
  subject,
  onClose,
}: Omit<NotifyStaffModalProps, "isOpen">) => {
  const { user } = useAuth();
  const selfUserId = user?.profile?.id ?? null;
  const titleId = useId();
  const groupLabelId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, onClose, lockScroll: true });

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<NotifyStaffResponse | null>(null);
  // State lands a render late; a double click inside that window would push every coach twice.
  const sendingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    getOrganizationMembers()
      .then((list) => {
        if (cancelled) return;
        setMembers(list);
        setLoadState("ready");
      })
      .catch((err) => {
        console.warn("[NotifyStaffModal] Failed to load staff", err);
        if (!cancelled) setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const choices = useMemo(() => excludeSelf(members, selfUserId), [members, selfUserId]);
  const atLimit = selected.length >= MAX_NOTIFY_RECIPIENTS;

  const toggle = useCallback((memberId: string) => {
    setSelected((current) => {
      if (current.includes(memberId)) return current.filter((id) => id !== memberId);
      if (current.length >= MAX_NOTIFY_RECIPIENTS) return current;
      return [...current, memberId];
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (sendingRef.current || selected.length === 0) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    try {
      const response = await notifyStaff(prospectId, {
        itemType,
        itemId,
        recipientUserIds: selected,
      });
      setResult(response);
    } catch (err) {
      console.warn("[NotifyStaffModal] Notify failed", err);
      setSendError(sendErrorMessage(err));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [selected, prospectId, itemType, itemId]);

  const noAlert = result ? buildNoAlertMessage(result.noDeviceUserIds, members) : null;
  const itemLabel = itemType === "video" ? "Video" : "Note";

  return createPortal(
    // No click-outside dismissal: a coach mid-selection loses their picks to a stray click, and a
    // mouse handler on a bare div has no keyboard equivalent. Escape, the close button and Cancel
    // all dismiss it.
    <div className="notify-staff-modal-overlay">
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="notify-staff-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="notify-staff-modal__header">
          <div>
            <h2 id={titleId} className="notify-staff-modal__title">
              Notify a coach
            </h2>
            {subject ? (
              <p className="notify-staff-modal__subtitle">
                {itemLabel} on {subject}
              </p>
            ) : null}
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {result ? (
          <div className="notify-staff-modal__body">
            <div role="status" className="notify-staff-modal__result">
              <p className="notify-staff-modal__result-title">Sent</p>
              {noAlert ? <p className="notify-staff-modal__result-note">{noAlert}</p> : null}
            </div>
            <div className="notify-staff-modal__actions">
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="notify-staff-modal__body">
            <p className="notify-staff-modal__hint">
              They will get a phone alert that opens this {itemLabel.toLowerCase()}.
            </p>

            {loadState === "loading" && (
              <p className="notify-staff-modal__status">Loading your staff...</p>
            )}

            {loadState === "error" && (
              <div className="notify-staff-modal__status">
                <p role="alert">Could not load your staff.</p>
                <Button variant="secondary" onClick={() => setLoadAttempt((n) => n + 1)}>
                  Try again
                </Button>
              </div>
            )}

            {loadState === "ready" && choices.length === 0 && (
              <p className="notify-staff-modal__status">
                There are no other coaches on your staff yet.
              </p>
            )}

            {loadState === "ready" && choices.length > 0 && (
              <>
                <span id={groupLabelId} className="notify-staff-modal__group-label">
                  Coaches
                </span>
                <div
                  role="group"
                  aria-labelledby={groupLabelId}
                  className="notify-staff-modal__list"
                >
                  {choices.map((member) => {
                    const checked = selected.includes(member.id);
                    return (
                      <Checkbox
                        key={member.id}
                        className="notify-staff-modal__option"
                        label={member.displayName}
                        checked={checked}
                        disabled={!checked && atLimit}
                        onChange={() => toggle(member.id)}
                      />
                    );
                  })}
                </div>
                {atLimit && (
                  <p className="notify-staff-modal__hint">
                    You can notify up to {MAX_NOTIFY_RECIPIENTS} coaches at once.
                  </p>
                )}
              </>
            )}

            {sendError && (
              <p role="alert" className="notify-staff-modal__error">
                {sendError}
              </p>
            )}

            <div className="notify-staff-modal__actions">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={selected.length === 0}
                loading={sending}
              >
                {selected.length > 1 ? `Send to ${selected.length} coaches` : "Send"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
